import React from 'react';
import { useTranslation } from 'react-i18next';
import { isNewSong, isRecentlyUpdated, wasContentUpdated, getRelativeTime } from '../../helpers/songDates';
import { formatNumber } from '../../utils/format';
import './SongBadges.scss';

const SongBadges = ({
  song,
  popularityData,
  hidePopularityBadge = false,
  showPlayCount = false,
  showUpdatedTime = false,
  hideUpdatedBadge = false,
  className = '',
  extraBadges = null,
}) => {
  const { t } = useTranslation();

  const renderPlayCountBadge = (song) => {
    if (!showPlayCount) {
      return null;
    }

    const plays = song.stats?.timesPlayed || 0;
    const formattedPlays = formatNumber(plays);

    return (
      <span
        className="play-count-badge"
        title={t('badges.playedTimes', { count: plays, formattedCount: formattedPlays })}
      >
        {t('badges.plays', { count: plays, formattedCount: formattedPlays })}
      </span>
    );
  };

  const renderPopularityBadge = (song) => {
    // Don't show if explicitly hidden (e.g., when sorting by popularity)
    if (hidePopularityBadge) {
      return null;
    }

    // Only show popularity badges when we have valid popularityData from GlobalContext
    // Don't show badges on page refresh when we don't have the full dataset loaded
    if (!popularityData) {
      return null;
    }

    const { maxPlays, popularSongs, mostPlayedOnly } = popularityData;
    const isPopular = popularSongs.some((popularSong) => popularSong._id === song._id);
    const hasBeenPlayed = maxPlays > 0;

    if (isPopular && hasBeenPlayed && mostPlayedOnly) {
      return (
        <span className="popularity-badge" title={t('badges.mostPlayedTitle')}>
          {t('badges.mostPlayed')}
        </span>
      );
    }

    if (isPopular && hasBeenPlayed) {
      // Show "TOP 10+" if ties push count beyond 10
      const badgeText = popularSongs.length > 10 ? t('badges.top10Plus') : t('badges.top10');

      let tooltipText;
      if (popularSongs.length > 10) {
        const tiedCount = popularSongs.length - 10;
        tooltipText = t('badges.top10TiedTitle', { count: tiedCount });
      } else {
        tooltipText = t('badges.topTitle', { count: popularSongs.length });
      }

      return (
        <span className="popularity-badge" title={tooltipText}>
          {badgeText}
        </span>
      );
    }
    return null;
  };

  const renderNewBadge = (song) => {
    if (isNewSong(song.createdAt, song.isNewSong)) {
      return (
        <span className="new-badge" title={t('badges.newTitle')}>
          {t('badges.new')}
        </span>
      );
    }
    return null;
  };

  const renderUpdatedBadge = (song) => {
    // Only show UPDATED badge if we have the reliable contentUpdatedAt field
    // This prevents false positives from updatedAt which includes stats updates
    if (hideUpdatedBadge || !song.contentUpdatedAt) {
      return null;
    }

    // Always show the UPDATED badge if filterring by recently updated songs
    const shouldShow = showUpdatedTime
      ? wasContentUpdated(song.createdAt, song.contentUpdatedAt)
      : isRecentlyUpdated(song.createdAt, song.contentUpdatedAt);

    if (shouldShow) {
      const relativeTime = getRelativeTime(song.contentUpdatedAt);
      return (
        <span className="updated-badge" title={t('badges.updatedTitle')}>
          {t('badges.updated', { time: relativeTime })}
        </span>
      );
    }
    return null;
  };

  const renderHiddenBadge = (song) => {
    if (song.isHidden) {
      return (
        <span className="hidden-badge" title={t('badges.hiddenTitle')}>
          {t('badges.hidden')}
        </span>
      );
    }
    return null;
  };

  const renderDraftBadge = (song) => {
    if (song.isDraft) {
      return (
        <span className="draft-badge" title={t('badges.draftTitle')}>
          {t('badges.draft')}
        </span>
      );
    }
    return null;
  };

  // Check if any badges should be shown. extraBadges counts here too: callers pass state
  // indicators (e.g. the overview pill) that must survive songs carrying no badges of their own.
  const hasAnyBadge =
    extraBadges ||
    renderHiddenBadge(song) ||
    renderPlayCountBadge(song) ||
    renderPopularityBadge(song) ||
    renderNewBadge(song) ||
    renderUpdatedBadge(song) ||
    renderDraftBadge(song);

  if (!hasAnyBadge) return null;

  return (
    <div className={`SongBadges ${className}`}>
      {extraBadges}
      {renderHiddenBadge(song)}
      {renderPlayCountBadge(song)}
      {renderPopularityBadge(song)}
      {renderNewBadge(song)}
      {renderUpdatedBadge(song)}
      {renderDraftBadge(song)}
    </div>
  );
};

export default SongBadges;
